import { Flex } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { transformChunks } from "shared/components/TextWrapper";
import { Button } from "ui/Button/Button";
import { InfoIcon } from "ui/Icons/Icons";
import { Modal, ModalProps } from "ui/Modal/Modal";
import { BodyM, HeaderS } from "ui/Typography/Typography";
import { isNotNullNorUndefined } from "utils/tsHelpers";

type ItemNotFoundModalProps = {
  pickedQuantity: number;
  totalQuantity: number;
  availableQuantity: number;
  onConfirmRefund: () => void;
} & Pick<ModalProps, "onClose" | "isOpen">;

export function ItemNotFoundModal({
  isOpen = false,
  onClose,
  pickedQuantity,
  totalQuantity,
  availableQuantity,
  onConfirmRefund,
}: ItemNotFoundModalProps) {
  const intl = useIntl();
  const refundAmount = totalQuantity - pickedQuantity;
  const showAvailableQuantity =
    isNotNullNorUndefined(availableQuantity) && availableQuantity >= totalQuantity;

  return (
    <Modal
      trapFocus={false}
      closeOnOverlayClick={false}
      isCentered
      isOpen={isOpen}
      onClose={onClose}
    >
      <Modal.Content marginX="s300">
        <Modal.Header textAlign="center" paddingX="s200">
          <HeaderS>
            {intl.formatMessage({ id: "component.picking.refund-process-modal.header" })}
          </HeaderS>
        </Modal.Header>
        <Modal.Body paddingY="0" paddingX="s200">
          <BodyM textAlign="center" color="grey.700">
            <FormattedMessage
              id="component.picking.refund-process-modal.subtitle"
              values={{
                picked: pickedQuantity,
                total: totalQuantity,
                ...transformChunks({ b: { as: "strong" } }),
              }}
            />
          </BodyM>
          {showAvailableQuantity && (
            <Flex mt="s200" p="s100" bg="orange.100" borderRadius="8px" alignItems="center">
              <InfoIcon marginX="s100" color="orange.500" boxSize="20px" />
              <Flex flexDirection="column">
                <BodyM color="orange.500" fontWeight={500}>
                  <FormattedMessage id="component.picking.refund-process-modal.stock-alert.title" />
                </BodyM>
                <BodyM color="grey.800">
                  <FormattedMessage
                    id="component.picking.refund-process-modal.stock-alert.body"
                    values={{
                      availableQuantity,
                      ...transformChunks({ b: { fontWeight: 700 } }),
                    }}
                  />
                </BodyM>
              </Flex>
            </Flex>
          )}
        </Modal.Body>
        <Modal.Footer padding="s200" flexDirection="column" gap="s100">
          <Button onClick={onConfirmRefund} isFullWidth size="lg" data-testid="ctaButton">
            {intl.formatMessage(
              { id: "component.picking.refund-process-modal.button" },
              { amount: refundAmount },
            )}
          </Button>
          <Button
            onClick={onClose}
            isFullWidth
            size="lg"
            variant="secondary"
            data-testid="cancelButton"
          >
            <FormattedMessage id="component.picking.refund-process-modal.cancel" />
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
